// Clever is English-only, so we don't translate these strings yet!
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {calculateNextUrl, reauthWithThirdParty} from "@khan/auth-shared";
import {requestCanonicalSubdomain} from "@khan/i18n";
import {PermissionsInfo} from "@khan/shared-core";
import {parseUrl, redirect, urlInLanguage, useLocation} from "@khan/urls";

// eslint-disable-next-line static-service/require-shared-package-imports-only -- TODO: Port this over to component-screens.
import LogoutRequired from "~/app-shell-package/errors/logout-required.tsx";
import {useApolloGqlClient} from "~/data-access/gql/use-apollo-gql-client.ts";
import {getLocaleStatus} from "~/shared-package/locale-status.ts";

import CleverLibraryOnboarding from "./clever-library-onboarding.tsx";
import {
    loginWithClever,
    transferCleverAuth,
    type CleverResult,
} from "./clever-login.ts";
import {getCleverUserNextUrl} from "./utils.ts";

export default function CleverRedirect({
    redirectType,
}: {
    redirectType: string | undefined;
}) {
    const {gqlClient} = useApolloGqlClient();
    const location = useLocation();
    const mountedRef = useRef(false);
    const [state, setState] = useState({
        cleverId: "",
        cleverEmail: undefined as string | undefined,
        isTeacher: false,
        isError: false,
        errorMessage: "",
        transferToken: "",
    });

    const handleCleverAuthError = useCallback((error: Error) => {
        if (mountedRef.current) {
            setState((prev) => ({
                ...prev,
                isError: true,
                errorMessage: error.message,
            }));
        }
    }, []);

    const handleReauth = useCallback(
        (nextUrl: string, isLibraryUser: boolean, transferToken: string) => {
            const divider = nextUrl.includes("?") ? "&" : "?";
            reauthWithThirdParty(
                gqlClient,
                isLibraryUser ? "clever_library" : "clever_district",
                transferToken,
            )
                .catch(() => {
                    if (
                        // TODO: Handle clever redirect to new classroom
                        // settings pages. Route name might be learner/account
                        // or teacher/account or learner/profile or
                        // teacher/profile
                        nextUrl.includes("/settings/account") ||
                        nextUrl.includes("/khanmigo/teacher-tools")
                    ) {
                        nextUrl +=
                            divider + "messageKey=clever_reauth_unsuccessful";
                        redirect(nextUrl);
                    } else {
                        nextUrl = "";
                        if (mountedRef.current) {
                            setState((prev) => ({
                                ...prev,
                                isError: true,
                            }));
                        }
                    }
                })
                .then(() => {
                    if (
                        nextUrl.includes("/settings/account") ||
                        nextUrl.includes("/khanmigo/teacher-tools")
                    ) {
                        nextUrl +=
                            divider + "messageKey=successfully_reauthenticated";
                    }
                    redirect(nextUrl);
                });
        },
        [gqlClient],
    );

    const handleCleverAuthResult = useCallback(
        async (
            result: CleverResult,
            continueUrl: string,
            isReauthVal: boolean,
        ) => {
            if (result.resultType === "success") {
                let nextUrl = await calculateNextUrl({
                    gqlClient,
                    location,
                    userData: result.user,
                    loginType: result.isLibraryUser
                        ? "clever"
                        : "clever_district",
                    continueUrl,
                    isFirstLogin: false,
                });
                const UDIs = result.user.userDistrictInfos;

                const divider = nextUrl.includes("?") ? "&" : "?";

                if (isReauthVal) {
                    if (redirectType?.startsWith("transfer_auth")) {
                        return handleReauth(
                            nextUrl,
                            // TODO(marksandstrom) These values are expected
                            // to be defined when the redirect type is
                            // transfer_auth, fix this. Anyway, this is
                            // better than the existing type suppression.
                            result.isLibraryUser ?? false,
                            result.transferToken ?? "",
                        );
                    }

                    if (
                        nextUrl.includes("/settings/account") ||
                        nextUrl.includes("/khanmigo/teacher-tools")
                    ) {
                        nextUrl +=
                            divider + "messageKey=successfully_reauthenticated";
                    }
                }

                const redirectURL = await getCleverUserNextUrl(
                    gqlClient,
                    nextUrl,
                    UDIs,
                );

                redirect(redirectURL);
            } else {
                if (result.udiUuid) {
                    const locale = result.kaLocale || "en";
                    const cleverID = result.cleverUserInfo
                        ? result.cleverUserInfo.cleverId
                        : "";

                    const languageTag = requestCanonicalSubdomain(
                        undefined,
                        locale,
                        getLocaleStatus(locale),
                    );
                    const activationURL = urlInLanguage(
                        languageTag,
                        parseUrl(
                            `/districts/link?udi=${result.udiUuid}&cleverId=${cleverID}`,
                        ),
                    );
                    redirect(activationURL);
                } else if (result.cleverUserInfo && mountedRef.current) {
                    setState((prev) => ({
                        ...prev,
                        cleverId: result.cleverUserInfo.cleverId,
                        cleverEmail: result.cleverUserInfo.cleverEmail,
                        isTeacher: result.cleverUserInfo.isTeacher,
                        transferToken: result.transferToken || "",
                    }));
                }
            }
        },
        [gqlClient, location, redirectType, handleReauth],
    );

    // Attempt to login with Clever on mount only
    useEffect(() => {
        mountedRef.current = true;
        const params = location.queryParams;
        const reauth = redirectType?.startsWith("reauth") ?? false;
        const continueUrl = params["state"] || "/";

        if (redirectType?.startsWith("transfer_auth")) {
            const cleverLoginOptions = {
                transferToken: params["transfer_token"] || "",
                reauth,
            } as const;
            transferCleverAuth(gqlClient, cleverLoginOptions)
                .then((result) =>
                    handleCleverAuthResult(result, continueUrl, reauth),
                )
                .catch(handleCleverAuthError);
        } else {
            const cleverLoginOptions = {
                code: params["code"] || "",
                reauth,
                isMap: redirectType?.includes("_map") ?? false,
                isLP: redirectType?.includes("_lp") ?? false,
                fromDevApp: redirectType?.includes("_dev") ?? false,
                referrerUrl: location.origin,
            } as const;
            loginWithClever(gqlClient, cleverLoginOptions)
                .then((result) =>
                    handleCleverAuthResult(result, continueUrl, reauth),
                )
                .catch(handleCleverAuthError);
        }

        return () => {
            mountedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this on mount
    }, []);

    const translateErrorMessage = useCallback(() => {
        const {errorMessage} = state;
        if (errorMessage === "INTERNAL") {
            return t`Unexpected error occurred`;
        }

        if (errorMessage === "INVALID_TOKEN") {
            return t`Unable to connect to Clever`;
        }

        if (errorMessage === "MISSING_TOKEN") {
            return t`No Clever data received`;
        }

        return errorMessage;
    }, [state]);

    if (PermissionsInfo.isLoggedIn) {
        return <LogoutRequired />;
    }

    if (state.cleverId) {
        return (
            <CleverLibraryOnboarding
                isTeacher={state.isTeacher}
                cleverId={state.cleverId}
                cleverEmail={state.cleverEmail || ""}
                transferToken={state.transferToken}
            />
        );
    }

    if (state.isError) {
        const {errorMessage} = state;
        return (
            <>
                <View style={styles.errorContainer}>
                    <HeadingMedium>{t`Uh oh!`}</HeadingMedium>
                    <Body tag="p">
                        {t`We weren’t able to bring you into Khan Academy (connection issue). From your Clever portal, try selecting the Khan Academy app again. If you still have trouble, please reach out to our Support team!`}
                    </Body>
                    <Strut size={spacing.medium_16} />
                    <Button
                        kind="secondary"
                        href="https://khanacademy.zendesk.com/hc/requests/new"
                        target="_blank"
                    >
                        {t`Contact Support`}
                    </Button>
                    {!!errorMessage && (
                        <>
                            <Strut size={spacing.large_24} />
                            <LabelSmall style={{color: color.offBlack64}}>
                                {t`Error message: ${translateErrorMessage()}`}
                            </LabelSmall>
                        </>
                    )}
                </View>
            </>
        );
    }

    return (
        <View style={styles.container}>
            <CircularSpinner size="large" style={styles.spinner} />
        </View>
    );
}

const styles = StyleSheet.create({
    spinner: {
        alignItems: "center",
    },
    container: {
        paddingBlockStart: spacing.xLarge_32,
    },
    errorContainer: {
        width: "75%",
        padding: spacing.xLarge_32,
        margin: "auto",
    },
});
